/**
 * Slice E3 — roster fatigue/hours threshold profiles (SHADOW mode only).
 *
 * Hardcoded platform literals (10h break / 12h max shift / 38h week) remain the
 * acted-on verdict. Profile-based verdicts are computed alongside and divergences
 * are recorded — the valve is not flipped in this slice.
 */

/** Acted-on platform literals — today's behaviour, named and visible. */
export const HARDCODED_ROSTER_THRESHOLDS = {
  minBreakHours: 10,
  maxShiftHours: 12,
  maxWeekHours: 38,
} as const;

export type RosterThresholdNumbers = {
  minBreakHours: number;
  maxShiftHours: number;
  maxWeekHours: number;
};

export type RosterThresholdProfile = {
  id: string;
  tenantId: string | null;
  profileKey: string;
  awardCode: string | null;
  agreementId: string | null;
  minBreakHours: number;
  maxShiftHours: number;
  maxWeekHours: number;
  effectiveFrom: string; // YYYY-MM-DD
  effectiveTo: string | null; // YYYY-MM-DD exclusive-end or null = open
};

export const PLATFORM_DEFAULT_PROFILE_KEY = 'PLATFORM_DEFAULT';

/**
 * Resolve the applicable profile for (tenant, award, agreement, date).
 * Effective-date filtered. Never returns null — falls back to PLATFORM_DEFAULT
 * numbers even when the catalog is empty.
 */
export function resolveRosterThresholdProfile(input: {
  tenantId: string;
  awardCode?: string | null;
  agreementId?: string | null;
  asOfDate: string; // YYYY-MM-DD
  profiles: readonly RosterThresholdProfile[];
}): RosterThresholdProfile {
  const asOf = input.asOfDate;
  const active = input.profiles.filter((p) => isEffectiveOn(p, asOf));

  const agreementMatch = active.find(
    (p) =>
      p.tenantId === input.tenantId &&
      input.agreementId &&
      p.agreementId === input.agreementId &&
      (!p.awardCode || p.awardCode === input.awardCode),
  );
  if (agreementMatch) return agreementMatch;

  const awardMatch = active.find(
    (p) =>
      p.tenantId === input.tenantId &&
      input.awardCode &&
      p.awardCode === input.awardCode &&
      !p.agreementId,
  );
  if (awardMatch) return awardMatch;

  const tenantDefault = active.find(
    (p) =>
      p.tenantId === input.tenantId &&
      p.profileKey === PLATFORM_DEFAULT_PROFILE_KEY &&
      !p.awardCode &&
      !p.agreementId,
  );
  if (tenantDefault) return tenantDefault;

  const platformDefault = active.find(
    (p) =>
      p.tenantId == null &&
      p.profileKey === PLATFORM_DEFAULT_PROFILE_KEY &&
      !p.awardCode &&
      !p.agreementId,
  );
  if (platformDefault) return platformDefault;

  // Hard fallback — never NULL.
  return {
    id: 'hardcoded-fallback',
    tenantId: null,
    profileKey: PLATFORM_DEFAULT_PROFILE_KEY,
    awardCode: null,
    agreementId: null,
    ...HARDCODED_ROSTER_THRESHOLDS,
    effectiveFrom: '1900-01-01',
    effectiveTo: null,
  };
}

function isEffectiveOn(profile: RosterThresholdProfile, asOfDate: string): boolean {
  if (profile.effectiveFrom > asOfDate) return false;
  if (profile.effectiveTo != null && profile.effectiveTo <= asOfDate) return false;
  return true;
}

export type FatigueVerdict = {
  restBreakOk: boolean;
  shiftHoursOk: boolean;
  weekHoursOk: boolean;
  thresholds: RosterThresholdNumbers;
};

export type FatigueShadowResult = {
  /** Acted-on verdict — always hardcoded thresholds in this slice. */
  acted: FatigueVerdict;
  /** Profile-based verdict — recorded only; never acts. */
  shadow: FatigueVerdict;
  diverges: boolean;
  profile: RosterThresholdProfile;
};

/**
 * SHADOW fatigue evaluation. Acts on HARDCODED_ROSTER_THRESHOLDS; computes the
 * profile-based verdict alongside. Callers must act on `acted` only.
 */
export function evaluateFatigueShadow(input: {
  breakHoursBetweenShifts: number | null;
  shiftPaidHours: number;
  weekPaidHours: number;
  profile: RosterThresholdProfile;
}): FatigueShadowResult {
  const actedThresholds: RosterThresholdNumbers = { ...HARDCODED_ROSTER_THRESHOLDS };
  const shadowThresholds: RosterThresholdNumbers = {
    minBreakHours: input.profile.minBreakHours,
    maxShiftHours: input.profile.maxShiftHours,
    maxWeekHours: input.profile.maxWeekHours,
  };

  const acted = applyThresholds(
    input.breakHoursBetweenShifts,
    input.shiftPaidHours,
    input.weekPaidHours,
    actedThresholds,
  );
  const shadow = applyThresholds(
    input.breakHoursBetweenShifts,
    input.shiftPaidHours,
    input.weekPaidHours,
    shadowThresholds,
  );

  return {
    acted,
    shadow,
    diverges:
      acted.restBreakOk !== shadow.restBreakOk ||
      acted.shiftHoursOk !== shadow.shiftHoursOk ||
      acted.weekHoursOk !== shadow.weekHoursOk,
    profile: input.profile,
  };
}

function applyThresholds(
  breakHoursBetweenShifts: number | null,
  shiftPaidHours: number,
  weekPaidHours: number,
  thresholds: RosterThresholdNumbers,
): FatigueVerdict {
  return {
    restBreakOk:
      breakHoursBetweenShifts == null || breakHoursBetweenShifts >= thresholds.minBreakHours,
    shiftHoursOk: shiftPaidHours <= thresholds.maxShiftHours,
    weekHoursOk: weekPaidHours <= thresholds.maxWeekHours,
    thresholds,
  };
}

/** Build a PLATFORM_DEFAULT seed profile with current hardcoded literals. */
export function platformDefaultThresholdProfile(
  overrides?: Partial<RosterThresholdProfile>,
): RosterThresholdProfile {
  return {
    id: overrides?.id ?? 'platform-default',
    tenantId: overrides?.tenantId ?? null,
    profileKey: PLATFORM_DEFAULT_PROFILE_KEY,
    awardCode: null,
    agreementId: null,
    ...HARDCODED_ROSTER_THRESHOLDS,
    effectiveFrom: overrides?.effectiveFrom ?? '1900-01-01',
    effectiveTo: overrides?.effectiveTo ?? null,
  };
}
